import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, GripVertical, X, Copy, Eye, Save, Plus, Filter, Package, ChevronDown, ChevronUp, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  adminGetProtocolBuilder, adminUpdateProtocolProducts, adminDuplicateProtocol,
  adminGetProducts, adminGetProductUsage, adminGetProductVendors,
  adminGetProtocols, adminGetConditions,
} from '@/lib/api';

const ROLES = [
  { id: 'primary', label: 'Core / Primary', color: 'bg-[#1a1a1a] text-white', max: 3 },
  { id: 'secondary', label: 'Targeted / Secondary', color: 'bg-[#d4e4bc] text-[#1a1a1a]', max: 4 },
  { id: 'optional', label: 'Optional', color: 'bg-gray-100 text-gray-700', max: 3 },
];

const AdminProtocolBuilder = () => {
  const { protocolId } = useParams();
  const navigate = useNavigate();
  
  // Data states
  const [protocol, setProtocol] = useState(null);
  const [assignedProducts, setAssignedProducts] = useState({ primary: [], secondary: [], optional: [] });
  const [allProducts, setAllProducts] = useState([]);
  const [productUsage, setProductUsage] = useState({});
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialogs
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [protocolListOpen, setProtocolListOpen] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [conditions, setConditions] = useState([]);
  
  // Drag state
  const dragItem = useRef(null);
  const dragSource = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [builderData, productsData, usageData, vendorsData] = await Promise.all([
        protocolId ? adminGetProtocolBuilder(protocolId) : null,
        adminGetProducts(),
        adminGetProductUsage(),
        adminGetProductVendors(),
      ]);
      
      if (builderData) {
        setProtocol(builderData.protocol);
        const grouped = { primary: [], secondary: [], optional: [] };
        (builderData.products || []).forEach(pp => {
          const role = pp.role || 'optional';
          if (grouped[role]) {
            grouped[role].push({ ...pp.product, pp_id: pp.id, role, sort_order: pp.sort_order });
          }
        });
        setAssignedProducts(grouped);
      }
      
      setAllProducts(productsData || []);
      setProductUsage(usageData?.product_usage || {});
      setVendors(vendorsData?.vendors || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [protocolId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Get IDs of all assigned products
  const assignedIds = new Set([
    ...assignedProducts.primary.map(p => p.id),
    ...assignedProducts.secondary.map(p => p.id),
    ...assignedProducts.optional.map(p => p.id),
  ]);

  // Filter catalog products
  const filteredProducts = allProducts.filter(p => {
    if (assignedIds.has(p.id)) return false;
    if (!p.shopify_handle) return false;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || 
      (p.product_name || '').toLowerCase().includes(q) ||
      (p.shopify_handle || '').toLowerCase().includes(q) ||
      (p.vendor || '').toLowerCase().includes(q);
    const matchesVendor = !vendorFilter || (p.vendor || '').toLowerCase() === vendorFilter.toLowerCase();
    return matchesSearch && matchesVendor;
  });

  const addProduct = (product, role) => {
    setAssignedProducts(prev => ({
      ...prev,
      [role]: [...prev[role], { ...product, role }]
    }));
    setHasChanges(true);
  };

  const removeProduct = (productId, role) => {
    setAssignedProducts(prev => ({
      ...prev,
      [role]: prev[role].filter(p => p.id !== productId)
    }));
    setHasChanges(true);
  };

  const moveProduct = (productId, fromRole, toRole) => {
    const product = assignedProducts[fromRole].find(p => p.id === productId);
    if (!product) return;
    setAssignedProducts(prev => ({
      ...prev,
      [fromRole]: prev[fromRole].filter(p => p.id !== productId),
      [toRole]: [...prev[toRole], { ...product, role: toRole }]
    }));
    setHasChanges(true);
  };

  const reorderProduct = (role, fromIndex, toIndex) => {
    const items = [...assignedProducts[role]];
    const [moved] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, moved);
    setAssignedProducts(prev => ({ ...prev, [role]: items }));
    setHasChanges(true);
  };

  // Drag handlers
  const handleDragStart = (e, product, source) => {
    dragItem.current = product;
    dragSource.current = source;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e, targetRole) => {
    e.preventDefault();
    const product = dragItem.current;
    const source = dragSource.current;
    if (!product) return;

    if (source === 'catalog') {
      addProduct(product, targetRole);
    } else if (source !== targetRole) {
      moveProduct(product.id, source, targetRole);
    }
    dragItem.current = null;
    dragSource.current = null;
  };

  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  const handleSave = async () => {
    setSaving(true);
    try {
      const allItems = [];
      let sortIdx = 1;
      for (const role of ['primary', 'secondary', 'optional']) {
        for (const product of assignedProducts[role]) {
          allItems.push({ product_id: product.id, role, sort_order: sortIdx++ });
        }
      }
      await adminUpdateProtocolProducts(protocolId, allItems);
      setHasChanges(false);
      toast.success('Protocol products saved');
      // Refresh usage data
      const usageData = await adminGetProductUsage();
      setProductUsage(usageData?.product_usage || {});
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!duplicateName.trim()) return;
    try {
      const result = await adminDuplicateProtocol(protocolId, duplicateName);
      toast.success(`Protocol duplicated (${result.products_copied} products copied)`);
      setDuplicateOpen(false);
      setDuplicateName('');
      navigate(`/admin/protocol-builder/${result.new_protocol_id}`);
    } catch (error) {
      toast.error('Failed to duplicate');
    }
  };

  const loadProtocolList = async () => {
    try {
      const [protocolsData, conditionsData] = await Promise.all([
        adminGetProtocols(),
        adminGetConditions(),
      ]);
      setProtocols(protocolsData || []);
      setConditions(conditionsData || []);
    } catch (error) {
      toast.error('Failed to load protocols');
    }
  };

  const handleBulkAdd = (products, role) => {
    const newProducts = products.filter(p => !assignedIds.has(p.id));
    setAssignedProducts(prev => ({
      ...prev,
      [role]: [...prev[role], ...newProducts.map(p => ({ ...p, role }))]
    }));
    setHasChanges(true);
    toast.success(`${newProducts.length} products added`);
  };

  const getUsageInfo = (productId) => productUsage[productId] || [];
  const totalAssigned = assignedProducts.primary.length + assignedProducts.secondary.length + assignedProducts.optional.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Protocol list view (when no protocolId)
  if (!protocolId) {
    return <ProtocolListView onSelect={(id) => navigate(`/admin/protocol-builder/${id}`)} />;
  }

  return (
    <div className="h-full flex flex-col" data-testid="protocol-builder">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/protocol-builder')} data-testid="back-to-protocols">
              <ArrowLeft className="w-4 h-4 mr-2" /> Protocols
            </Button>
            <div>
              <h1 className="font-serif text-xl text-[#1a1a1a]">
                {protocol?.consumer_title || protocol?.protocol_name}
              </h1>
              <p className="text-xs text-gray-500">
                {protocol?.condition?.consumer_label} &middot; {totalAssigned} products assigned
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)} data-testid="preview-btn">
              <Eye className="w-4 h-4 mr-2" /> Preview
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setDuplicateName(`${protocol?.protocol_name} (Copy)`); setDuplicateOpen(true); }} data-testid="duplicate-btn">
              <Copy className="w-4 h-4 mr-2" /> Duplicate
            </Button>
            <Button onClick={handleSave} disabled={saving || !hasChanges} className="btn-primary" data-testid="save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {hasChanges ? 'Save Changes' : 'Saved'}
            </Button>
          </div>
        </div>
      </div>

      {/* Main content: two panels */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Product Catalog */}
        <div className="w-[380px] border-r border-gray-200 flex flex-col bg-gray-50">
          <div className="p-4 border-b border-gray-200 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10"
                data-testid="product-search"
              />
            </div>
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)} className="text-xs text-gray-600">
                <Filter className="w-3 h-3 mr-1" /> Filters {vendorFilter && '(1)'}
              </Button>
              <span className="text-xs text-gray-400">{filteredProducts.length} products</span>
            </div>
            {showFilters && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setVendorFilter('')}
                  className={`text-xs px-2.5 py-1 rounded-full transition-colors ${!vendorFilter ? 'bg-[#1a1a1a] text-white' : 'bg-white border text-gray-600 hover:bg-gray-100'}`}
                  data-testid="filter-all"
                >
                  All
                </button>
                {vendors.map(v => (
                  <button
                    key={v}
                    onClick={() => setVendorFilter(vendorFilter === v ? '' : v)}
                    className={`text-xs px-2.5 py-1 rounded-full transition-colors ${vendorFilter === v ? 'bg-[#1a1a1a] text-white' : 'bg-white border text-gray-600 hover:bg-gray-100'}`}
                    data-testid={`filter-${v.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product list */}
          <div className="flex-1 overflow-y-auto p-2">
            {filteredProducts.slice(0, 100).map(product => {
              const usage = getUsageInfo(product.id);
              return (
                <div
                  key={product.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, product, 'catalog')}
                  className="flex items-center gap-3 p-3 mb-1 bg-white rounded-lg border border-gray-100 cursor-grab hover:border-gray-300 hover:shadow-sm transition-all group"
                  data-testid={`catalog-product-${product.id}`}
                >
                  <GripVertical className="w-4 h-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
                  {product.image_url ? (
                    <img src={product.image_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0 bg-gray-100" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Package className="w-4 h-4 text-gray-300" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1a1a1a] truncate">{product.product_name}</p>
                    <div className="flex items-center gap-2">
                      {product.vendor && <span className="text-[10px] text-gray-400 uppercase">{product.vendor}</span>}
                      {product.price > 0 && <span className="text-xs text-gray-500">${product.price.toFixed(2)}</span>}
                    </div>
                    {usage.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {usage.map((u, i) => (
                          <span key={i} className="text-[9px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-500">{u.protocol_name}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Quick add buttons */}
                  <div className="hidden group-hover:flex flex-col gap-1">
                    {ROLES.map(r => (
                      <button
                        key={r.id}
                        onClick={(e) => { e.stopPropagation(); addProduct(product, r.id); }}
                        className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 whitespace-nowrap"
                        title={`Add as ${r.label}`}
                      >
                        + {r.id === 'primary' ? 'Core' : r.id === 'secondary' ? 'Targeted' : 'Opt'}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            {filteredProducts.length > 100 && (
              <p className="text-xs text-gray-400 text-center py-4">Showing 100 of {filteredProducts.length}. Use search to narrow down.</p>
            )}
            {filteredProducts.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-8">No products found</p>
            )}
          </div>
        </div>

        {/* Right: Protocol Sections */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {ROLES.map(roleConfig => {
            const products = assignedProducts[roleConfig.id];
            return (
              <div
                key={roleConfig.id}
                onDrop={(e) => handleDrop(e, roleConfig.id)}
                onDragOver={handleDragOver}
                className="border border-dashed border-gray-300 rounded-xl p-4 min-h-[120px] transition-colors"
                data-testid={`drop-zone-${roleConfig.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className={roleConfig.color}>{roleConfig.label}</Badge>
                    <span className="text-xs text-gray-500">{products.length} / {roleConfig.max} recommended</span>
                  </div>
                  {products.length > roleConfig.max && (
                    <span className="text-xs text-amber-600">Over recommended limit</span>
                  )}
                </div>

                {products.length === 0 ? (
                  <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
                    <Plus className="w-4 h-4 mr-2" /> Drag products here or use the + buttons
                  </div>
                ) : (
                  <div className="space-y-2">
                    {products.map((product, index) => {
                      const usage = getUsageInfo(product.id).filter(u => u.protocol_id !== protocolId);
                      return (
                        <div
                          key={product.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, product, roleConfig.id)}
                          className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 group cursor-grab"
                          data-testid={`assigned-${roleConfig.id}-${product.id}`}
                        >
                          <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
                          <span className="text-xs text-gray-400 w-5">{index + 1}</span>
                          {product.image_url ? (
                            <img src={product.image_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <Package className="w-4 h-4 text-gray-300" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-[#1a1a1a] truncate">{product.product_name}</p>
                            <div className="flex items-center gap-2">
                              {product.vendor && <span className="text-[10px] text-gray-400 uppercase">{product.vendor}</span>}
                              {product.price > 0 && <span className="text-xs text-gray-500">${product.price.toFixed(2)}</span>}
                            </div>
                            {usage.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                <span className="text-[9px] text-gray-400">Also in:</span>
                                {usage.map((u, i) => (
                                  <span key={i} className="text-[9px] px-1 py-0.5 bg-blue-50 rounded text-blue-600">{u.protocol_name}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* Move between roles */}
                          <div className="hidden group-hover:flex items-center gap-1">
                            {ROLES.filter(r => r.id !== roleConfig.id).map(r => (
                              <button
                                key={r.id}
                                onClick={() => moveProduct(product.id, roleConfig.id, r.id)}
                                className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                                title={`Move to ${r.label}`}
                              >
                                {r.id === 'primary' ? 'Core' : r.id === 'secondary' ? 'Tgt' : 'Opt'}
                              </button>
                            ))}
                          </div>
                          <button
                            onClick={() => removeProduct(product.id, roleConfig.id)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
                            title="Remove"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Duplicate Dialog */}
      <Dialog open={duplicateOpen} onOpenChange={setDuplicateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">Duplicate Protocol</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm text-gray-600 mb-2 block">New protocol name</label>
            <Input
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              placeholder="e.g., GERD Support Protocol"
              data-testid="duplicate-name-input"
            />
            <p className="text-xs text-gray-500 mt-2">All product assignments will be copied to the new protocol.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateOpen(false)}>Cancel</Button>
            <Button onClick={handleDuplicate} className="btn-primary" data-testid="confirm-duplicate-btn">
              <Copy className="w-4 h-4 mr-2" /> Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {protocol?.consumer_title || protocol?.protocol_name}
            </DialogTitle>
            <p className="text-sm text-gray-500">{protocol?.description}</p>
          </DialogHeader>
          <div className="py-4 space-y-6">
            {ROLES.map(roleConfig => {
              const products = assignedProducts[roleConfig.id];
              if (products.length === 0) return null;
              return (
                <div key={roleConfig.id}>
                  <p className="text-xs uppercase tracking-widest text-gray-500 font-medium mb-3">
                    {roleConfig.label}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {products.map(product => (
                      <div key={product.id} className="flex items-center gap-3 p-3 border border-[#d0e4d4] rounded-lg">
                        {product.image_url ? (
                          <img src={product.image_url} alt="" className="w-12 h-12 rounded object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center">
                            <Package className="w-5 h-5 text-gray-300" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#1a1a1a] truncate">{product.product_name}</p>
                          {product.price > 0 && <p className="text-xs text-gray-500">${product.price.toFixed(2)}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t pt-4">
            <Button className="w-full btn-primary" disabled>
              <Package className="w-4 h-4 mr-2" /> Add Full Protocol to Cart (Preview)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Protocol list component
const ProtocolListView = ({ onSelect }) => {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const data = await adminGetProtocols();
        setProtocols(data || []);
      } catch { /* */ } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="p-6" data-testid="protocol-list">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl text-[#1a1a1a] mb-1">Protocol Builder</h1>
          <p className="text-gray-500 text-sm">Select a protocol to manage its product assignments</p>
        </div>
      </div>
      <div className="grid gap-4 max-w-3xl">
        {protocols.map(proto => (
          <button
            key={proto.id}
            onClick={() => onSelect(proto.id)}
            className="flex items-center gap-4 p-5 bg-white border border-gray-200 rounded-xl text-left hover:border-[#1a1a1a] hover:shadow-sm transition-all group"
            data-testid={`protocol-select-${proto.id}`}
          >
            <div className="w-12 h-12 rounded-full bg-[#d4e4bc] flex items-center justify-center flex-shrink-0">
              <Package className="w-6 h-6 text-[#1a1a1a]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-lg text-[#1a1a1a] group-hover:text-[#1a1a1a]">
                {proto.consumer_title || proto.protocol_name}
              </h3>
              <p className="text-sm text-gray-500 truncate">{proto.description}</p>
              {proto.condition && (
                <span className="text-xs text-gray-400 mt-1 inline-block">
                  Condition: {proto.condition.consumer_label || proto.condition.name}
                </span>
              )}
            </div>
            <ArrowLeft className="w-5 h-5 text-gray-300 rotate-180 group-hover:text-[#1a1a1a] transition-colors" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default AdminProtocolBuilder;
